import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "src/i18n";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import {
  useFoodicsBranchMappings,
  useFoodicsBranches,
  useCreateFoodicsBranchMapping,
  useDeleteFoodicsBranchMapping,
} from "src/api/integrations/foodics";
import type { FoodicsBranch, FoodicsBranchMapping } from "src/types/foodics";
import { useGetBranches } from "src/api/generic";
import { notificationsState } from "src/store/notifications";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { NoData } from "src/shared/components/tables/no-data";
import { ConfirmPopup } from "src/shared/components/confirm-popup";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SyncIcon from "@mui/icons-material/Sync";

const FoodicsMappings = () => {
  const { t } = useTranslation("integrations");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const [notifications, setNotifications] = useRecoilState(notificationsState);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMapping, setSelectedMapping] =
    useState<FoodicsBranchMapping | null>(null);
  const [selectedJazrBranch, setSelectedJazrBranch] = useState<number | "">(
    ""
  );
  const [selectedFoodicsBranch, setSelectedFoodicsBranch] =
    useState<string>("");

  const {
    data: mappingsData,
    isLoading: mappingsLoading,
    refetch: refetchMappings,
  } = useFoodicsBranchMappings();
  const {
    data: foodicsBranchesData,
    isLoading: foodicsBranchesLoading,
    refetch: refetchFoodicsBranches,
  } = useFoodicsBranches();
  const { data: jazrBranchesData, isLoading: jazrBranchesLoading } =
    useGetBranches();
  const {
    mutate: createMapping,
    status: createStatus,
    reset: resetCreate,
  } = useCreateFoodicsBranchMapping();
  const {
    mutate: deleteMapping,
    status: deleteStatus,
    reset: resetDelete,
  } = useDeleteFoodicsBranchMapping();

  const mappings = mappingsData?.data || [];
  const foodicsBranches = foodicsBranchesData?.data || [];
  const jazrBranches = jazrBranchesData?.data || [];
  const currentLang = i18n.language || "en";

  const getFoodicsBranchName = (branch: FoodicsBranch): string => {
    if (currentLang === "ar" && branch.name_localized)
      return branch.name_localized;
    return branch.name || "-";
  };

  const getJazrBranchName = (
    name: string | { en: string; ar: string } | undefined
  ): string => {
    if (!name) return "-";
    if (typeof name === "string") return name;
    return name[currentLang as keyof typeof name] || name.en || "-";
  };

  const emptyDataArr = mappings.length === 0;

  const mappedJazrBranchIds = mappings.map((m) => m.internal_id);
  const unmappedJazrBranches = jazrBranches.filter(
    (b) => !mappedJazrBranchIds.includes(b.id)
  );

  const mappedFoodicsBranchIds = mappings.map((m) => m.external_id);
  const unmappedFoodicsBranches = foodicsBranches.filter(
    (b) => !mappedFoodicsBranchIds.includes(b.id)
  );

  const handleAddMapping = () => {
    if (!selectedJazrBranch || !selectedFoodicsBranch) return;

    const foodicsBranch = foodicsBranches.find(
      (b) => b.id === selectedFoodicsBranch
    );

    createMapping({
      internal_id: selectedJazrBranch as number,
      external_id: selectedFoodicsBranch,
      external_code: foodicsBranch?.reference,
      external_name: foodicsBranch
        ? getFoodicsBranchName(foodicsBranch)
        : undefined,
    });
  };

  const handleDeleteMapping = () => {
    if (!selectedMapping) return;
    deleteMapping(selectedMapping.id);
  };

  const handleOpenDeleteDialog = (mapping: FoodicsBranchMapping) => {
    setSelectedMapping(mapping);
    setDeleteDialogOpen(true);
  };

  const resetAddForm = () => {
    setSelectedJazrBranch("");
    setSelectedFoodicsBranch("");
    setAddDialogOpen(false);
    resetCreate();
  };

  useEffect(() => {
    if (createStatus === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("mappings.saveSuccess"),
        },
      ]);
      resetAddForm();
      refetchMappings();
    } else if (createStatus === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: t("mappings.saveError"),
        },
      ]);
    }
  }, [createStatus]);

  useEffect(() => {
    if (deleteStatus === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("mappings.deleteSuccess"),
        },
      ]);
      setDeleteDialogOpen(false);
      setSelectedMapping(null);
      resetDelete();
      refetchMappings();
    } else if (deleteStatus === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: t("mappings.deleteError"),
        },
      ]);
    }
  }, [deleteStatus]);

  const tableHeadText = [
    t("mappings.table.jazrBranch"),
    t("mappings.form.foodicsBranch"),
    t("mappings.table.externalId"),
    t("mappings.table.lastSynced"),
    t("mappings.table.actions"),
  ];

  return (
    <Stack gap={3} p={{ xs: 2, sm: "8px 32px 32px 32px" }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/integrations/foodics")}
            sx={{ color: "#667085" }}
          >
            {generalT("back")}
          </Button>
          <Box>
            <Typography fontSize={24} fontWeight={600} color="#101828">
              {t("mappings.title")}
            </Typography>
            <Typography fontSize={14} color="#667085">
              {t("mappings.subtitle")}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={
              foodicsBranchesLoading ? (
                <CircularProgress size={16} />
              ) : (
                <SyncIcon />
              )
            }
            onClick={() => refetchFoodicsBranches()}
            disabled={foodicsBranchesLoading}
          >
            {t("mappings.fetchFoodicsBranches")}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            disabled={
              unmappedJazrBranches.length === 0 ||
              unmappedFoodicsBranches.length === 0
            }
          >
            {t("mappings.addMapping")}
          </Button>
        </Stack>
      </Box>

      {/* Divider */}
      <Box bgcolor="#F2F4F7" width="100%" height="1px" />

      {/* Table */}
      <Box>
        <TableContainer component={Paper} variant="outlined">
          <Table sx={{ minWidth: 650 }} aria-label="mappings table">
            <TableHead>
              <TableRow>
                {tableHeadText.map((item) => (
                  <TableCell
                    key={item}
                    align="center"
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      lineHeight: "18px",
                      color: "#475467",
                      bgcolor: "#F9FAFB",
                    }}
                  >
                    {item}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {mappingsLoading ? (
              <TableLoadingSkeleton rowsLength={5} cellsLength={5} />
            ) : (
              <TableBody>
                {mappings.map((mapping) => (
                  <TableRow
                    key={mapping.id}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    <TableCell align="center">
                      {getJazrBranchName(mapping.internal_name) ||
                        mapping.internal_id}
                    </TableCell>
                    <TableCell align="center">
                      {getJazrBranchName(mapping.external_name) || "-"}
                    </TableCell>
                    <TableCell align="center">
                      {mapping.external_id}
                    </TableCell>
                    <TableCell align="center">
                      {mapping.synced_at
                        ? new Date(mapping.synced_at).toLocaleString()
                        : "-"}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleOpenDeleteDialog(mapping)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
          {!mappingsLoading && emptyDataArr && <NoData />}
        </TableContainer>
      </Box>

      {/* Add Mapping Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={resetAddForm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("mappings.addMapping")}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} mt={1}>
            <FormControl fullWidth>
              <InputLabel>{t("mappings.form.jazrBranch")}</InputLabel>
              <Select
                value={selectedJazrBranch}
                label={t("mappings.form.jazrBranch")}
                onChange={(e) =>
                  setSelectedJazrBranch(e.target.value as number)
                }
                disabled={jazrBranchesLoading}
              >
                <MenuItem value="" disabled>
                  {t("mappings.form.selectJazrBranch")}
                </MenuItem>
                {unmappedJazrBranches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {getJazrBranchName(branch.name)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t("mappings.form.foodicsBranch")}</InputLabel>
              <Select
                value={selectedFoodicsBranch}
                label={t("mappings.form.foodicsBranch")}
                onChange={(e) =>
                  setSelectedFoodicsBranch(e.target.value as string)
                }
                disabled={foodicsBranchesLoading}
              >
                <MenuItem value="" disabled>
                  {t("mappings.form.selectFoodicsBranch")}
                </MenuItem>
                {unmappedFoodicsBranches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {getFoodicsBranchName(branch)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAddForm}>{t("mappings.form.cancel")}</Button>
          <Button
            onClick={handleAddMapping}
            variant="contained"
            disabled={
              !selectedJazrBranch ||
              !selectedFoodicsBranch ||
              createStatus === "pending"
            }
          >
            {createStatus === "pending" ? (
              <CircularProgress size={20} />
            ) : (
              t("mappings.form.save")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmPopup
        open={deleteDialogOpen}
        handleClose={() => setDeleteDialogOpen(false)}
        handleConfirm={handleDeleteMapping}
        isPending={deleteStatus === "pending"}
        title={t("mappings.deleteMapping")}
        subtitle={t("mappings.deleteConfirm")}
        confirmBtnText={generalT("delete")}
      />
    </Stack>
  );
};

export default FoodicsMappings;
